import React, { useEffect, useRef, useState, useMemo } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import SignaturePad from "react-signature-canvas";
import DatePicker from "react-datepicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CustomDatePickerInput from "../_common/CustomDatePickerInput";
import AlertModal from "../_common/AlertModal";
import { useSelector } from "react-redux";
import AlertBox from "../_common/AlertBox";

function AdrTentativeAgreementSignModalV3(props) {
  const { showModal, toggleModal, forCustomTa, applySignature, ...rest } = props;

  const user = useSelector((state) => state.authentication.user || null);

  const hasPrevSignature = useMemo(
    () => rest.caltransRepSignedOn,
    [rest.caltransRepSignedOn]
  );

  const [showAlert_signatureEmpty, _setShowAlert_signatureEmpty] = useState(false);
  const toggleAlert_signatureEmpty = () =>
    _setShowAlert_signatureEmpty(!showAlert_signatureEmpty);

  const [showAlert_formIncomplete, _setShowAlert_formIncomplete] = useState(false);
  const toggleAlert_formIncomplete = () =>
    _setShowAlert_formIncomplete(!showAlert_formIncomplete);

  const [showAlert_deleteSignatureConfirm, _setShowAlert_deleteSignatureConfirm] =
    useState(false);
  const toggleAlert_deleteSignatureConfirm = () =>
    _setShowAlert_deleteSignatureConfirm(!showAlert_deleteSignatureConfirm);

  const [caltransRepFields, setCaltransRepFields] = useState({
    caltransRepSignature: rest.caltransRepSignature || "",
    isWetSignedByCaltransRep: rest.isWetSignedByCaltransRep,
    caltransRepSignedOn: rest.caltransRepSignedOn || null,
    caltransRepName: rest.caltransRepName || (user ? user.fullName : ""),
    caltransRepRemarks: rest.caltransRepRemarks || "",
    isDisputedByCaltransRep: rest.isDisputedByCaltransRep,
  });

  const [formErrors, setFormErrors] = useState({
    name: null,
    date: null,
  });

  const fieldsToValidate = useRef(["caltransRepName", "caltransRepSignedOn"]);

  useEffect(() => validateForm(), [caltransRepFields]);

  /** @type {React.MutableRefObject<SignaturePad>} */
  const caltransRepSignPad = useRef(null);

  const validateForm = () => {
    let _formErrors = { ...formErrors };

    const isWetSigned = caltransRepFields.isWetSignedByCaltransRep;

    fieldsToValidate.current.forEach((fieldToValidate) => {
      switch (fieldToValidate) {
        case "caltransRepSignedOn":
          if ((isWetSigned || forCustomTa) && !caltransRepFields.caltransRepSignedOn)
            _formErrors = { ..._formErrors, date: "Required" };
          else _formErrors = { ..._formErrors, date: null };
          break;
        case "caltransRepName":
          // For CEM4907, if signature is accepted or drawn, require name
          // For custom form, if signedOn is populated, require name
          // For either form type, if disputed, require name
          const caltransRepName = (caltransRepFields.caltransRepName || "").trim();
          if (
            (isWetSigned && !caltransRepName) ||
            (!isWetSigned &&
              ((!forCustomTa &&
                (caltransRepFields.caltransRepSignature ||
                  (caltransRepSignPad.current &&
                    !caltransRepSignPad.current.isEmpty())) &&
                !caltransRepName) ||
                (forCustomTa &&
                  caltransRepFields.caltransRepSignedOn &&
                  !caltransRepName)))
          )
            _formErrors = { ..._formErrors, name: "Required" };
          else _formErrors = { ..._formErrors, name: null };
          break;
        default:
          break;
      }
    });

    setFormErrors(_formErrors);
  };

  const formIsIncomplete = () => Object.values(formErrors).some((e) => e);

  const getUpdatedCaltransRepFieldsWithSignature = () => {
    const isWetSigned = caltransRepFields.isWetSignedByCaltransRep;
    // Create a temp store for the state
    const _caltransRepFields = { ...caltransRepFields };

    // For CEM4907, if signature is drawn but not accepted, accept it programmatically
    if (
      !forCustomTa &&
      !isWetSigned &&
      caltransRepSignPad.current &&
      !caltransRepSignPad.current.isEmpty() &&
      !caltransRepFields.caltransRepSignature
    ) {
      const ctSigDataURL = caltransRepSignPad.current
        .getTrimmedCanvas()
        .toDataURL("image/png");

      _caltransRepFields.caltransRepSignature = ctSigDataURL;
      _caltransRepFields.caltransRepSignedOn = Date.now();
    }

    _caltransRepFields.isDisputedByCaltransRep = false;

    return _caltransRepFields;
  };

  const handleClick_ok = (e) => {
    e.preventDefault();

    const isWetSigned = caltransRepFields.isWetSignedByCaltransRep;

    // Warn if user pressed OK without signing
    if (
      (!forCustomTa &&
        !isWetSigned &&
        caltransRepSignPad.current &&
        caltransRepSignPad.current.isEmpty()) ||
      (forCustomTa && !caltransRepFields.caltransRepSignedOn)
    ) {
      toggleAlert_signatureEmpty();
    } else if (formIsIncomplete()) {
      toggleAlert_formIncomplete();
    } else {
      const _caltransRepFields = getUpdatedCaltransRepFieldsWithSignature();
      applySignature(_caltransRepFields);
      toggleModal();
    }
  };

  const handleChange_caltransRep = (e) =>
    setCaltransRepFields({ ...caltransRepFields, [e.target.name]: e.target.value });

  const handleBlur_caltransRep = () => validateForm();

  const handleClick_clearCaltransRepSign = (e) => {
    e.preventDefault();
    setCaltransRepFields({
      ...caltransRepFields,
      caltransRepSignature: "",
      caltransRepSignedOn: null,
    });
    caltransRepSignPad.current.clear();
  };

  const handleClick_clearCaltransRepSign_img = (e) => {
    e.preventDefault();
    setCaltransRepFields({
      ...caltransRepFields,
      caltransRepSignature: "",
      caltransRepSignedOn: null,
    });
  };

  const handleChange_date = (value) =>
    setCaltransRepFields({ ...caltransRepFields, caltransRepSignedOn: value });

  const handleClick_acceptCaltransRepSign = (e) => {
    e.preventDefault();
    if (!caltransRepSignPad.current.isEmpty()) {
      const _caltransRepFields = getUpdatedCaltransRepFieldsWithSignature();
      setCaltransRepFields(_caltransRepFields);
    }
  };

  const handleClick_delete = (e) => {
    e.preventDefault();
    applySignature({
      caltransRepSignature: "",
      isWetSignedByCaltransRep: false,
      caltransRepSignedOn: null,
      caltransRepName: "",
      caltransRepRemarks: "",
      isDisputedByCaltransRep: false,
    });
    toggleModal();
  };

  const handleAlert_deleteSignatureConfirm = (actionType) => (e) => {
    e.preventDefault();
    if (actionType === "accept") {
      applySignature({
        ...caltransRepFields,
        caltransRepSignature: "",
        caltransRepSignedOn: null,
        caltransRepName: "",
        caltransRepRemarks: "",
        isDisputedByCaltransRep: false,
      });
      toggleAlert_deleteSignatureConfirm();
      toggleModal();
    }
    toggleAlert_deleteSignatureConfirm();
  };

  const handleAlert_signatureEmpty = (actionType) => (e) => {
    e.preventDefault();
    if (actionType === "accept") {
      applySignature({ ...caltransRepFields });
      toggleAlert_signatureEmpty();
      toggleModal();
    }
    toggleAlert_signatureEmpty();
  };

  const handleAlert_formIncomplete = (actionType) => (e) => {
    e.preventDefault();
    if (actionType === "accept") {
      const _caltransRepFields = getUpdatedCaltransRepFieldsWithSignature();
      applySignature(_caltransRepFields);
      toggleAlert_formIncomplete();
      toggleModal();
    }
    toggleAlert_formIncomplete();
  };

  const closeBtn = (
    <button className="close" onClick={toggleModal}>
      <FontAwesomeIcon icon="times" className="fa-fw text-white" />
    </button>
  );

  return (
    <Modal isOpen={showModal} size="md">
      <ModalHeader className="bg-info text-white" close={closeBtn}>
        <FontAwesomeIcon icon="file-signature" className="fa-fw mr-2" />
        Sign Tentative Agreement
      </ModalHeader>
      <ModalBody>
        <div className="card">
          <div className="card-body p-3">
            <h5 className="text-muted">Caltrans Representative</h5>
            {forCustomTa && (
              <AlertBox icon="exclamation-triangle" color="warning" className="mb-2">
                Please sign and attach the original copy to this TA.
              </AlertBox>
            )}
            {!forCustomTa && (
              <React.Fragment>
                <div className="form-row">
                  <div className="col">
                    <span>
                      <b>Signature:</b>
                    </span>
                  </div>
                </div>
                <div className="form-row">
                  <div className="col-12">
                    <div className="form-check-inline custom-control custom-radio mt-1 ml-2">
                      <div className="radio">
                        <input
                          type="radio"
                          name="isWetSignedByCaltransRep"
                          id="isWetSignedByCaltransRep"
                          checked={caltransRepFields.isWetSignedByCaltransRep}
                          onChange={(e) =>
                            e.target.checked &&
                            setCaltransRepFields({
                              ...caltransRepFields,
                              isWetSignedByCaltransRep: true,
                              caltransRepSignature: "",
                              caltransRepSignedOn: null,
                            })
                          }
                          className="custom-control-input"
                          disabled={hasPrevSignature}
                        />
                        <label
                          htmlFor="isWetSignedByCaltransRep"
                          className="custom-control-label my-auto"
                        >
                          Signed elsewhere (e.g. wet signature on hard copy)
                        </label>
                      </div>
                    </div>
                    <div className="form-check-inline custom-control custom-radio mt-1 ml-2">
                      <div className="radio">
                        <input
                          type="radio"
                          name="signUsingThisDevice"
                          id="signUsingThisDevice"
                          checked={!caltransRepFields.isWetSignedByCaltransRep}
                          onChange={(e) =>
                            e.target.checked &&
                            setCaltransRepFields({
                              ...caltransRepFields,
                              isWetSignedByCaltransRep: false,
                              caltransRepSignedOn: null,
                            })
                          }
                          className="custom-control-input"
                          disabled={hasPrevSignature}
                        />
                        <label
                          htmlFor="signUsingThisDevice"
                          className="custom-control-label my-auto"
                        >
                          Sign using this device (draw below):
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="col">
                    <div className="ta-sign__signpad">
                      {caltransRepFields.isWetSignedByCaltransRep ? (
                        <div className="m-2 text-small">
                          Provide the date of signature in "Signed On" field below, and
                          attach a photo/scan of the signed copy.
                        </div>
                      ) : caltransRepFields.caltransRepSignature ? (
                        <React.Fragment>
                          <img
                            src={caltransRepFields.caltransRepSignature}
                            alt="Caltrans Rep Signature"
                            style={{ width: "100%", height: "100%" }}
                          />
                          {!hasPrevSignature && (
                            <div className="ta-sign__signpad__controls">
                              <button
                                id="clearCaltransRepSign"
                                name="clearCaltransRepSign"
                                className="btn btn-sm btn-secondary mr-2"
                                onClick={handleClick_clearCaltransRepSign_img}
                                onTouchEnd={handleClick_clearCaltransRepSign_img}
                              >
                                Clear
                              </button>
                            </div>
                          )}
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <SignaturePad
                            canvasProps={{ style: { width: "100%", height: "100%" } }}
                            ref={(ref) => {
                              caltransRepSignPad.current = ref;
                            }}
                            onEnd={validateForm}
                          />
                          <div className="ta-sign__signpad__controls">
                            <button
                              id="clearCaltransRepSign"
                              name="clearCaltransRepSign"
                              className="btn btn-sm btn-outline-secondary mr-2"
                              onClick={handleClick_clearCaltransRepSign}
                              onTouchEnd={handleClick_clearCaltransRepSign}
                            >
                              Clear
                            </button>
                            <button
                              id="clearCaltransRepSign"
                              name="clearCaltransRepSign"
                              className="btn btn-sm btn-success"
                              onClick={handleClick_acceptCaltransRepSign}
                              onTouchEnd={handleClick_acceptCaltransRepSign}
                            >
                              Accept
                            </button>
                          </div>
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            )}
            <div className="form-row form-group mb-2">
              <label htmlFor="caltransRepSignedOn" className="col-form-label col-3">
                <b>Signed On:</b>
              </label>
              <div className="col-9">
                {!forCustomTa && !caltransRepFields.isWetSignedByCaltransRep ? (
                  <input
                    name="caltransRepSignedOn"
                    id="caltransRepSignedOn"
                    className="form-control"
                    value={
                      caltransRepFields.caltransRepSignedOn
                        ? new Date(caltransRepFields.caltransRepSignedOn).toLocaleString()
                        : ""
                    }
                    onChange={() => {}}
                    disabled={true}
                  />
                ) : (
                  <>
                    <DatePicker
                      customInput={<CustomDatePickerInput />}
                      name="reportStartDate"
                      id="reportStartDate"
                      selected={
                        caltransRepFields.caltransRepSignedOn
                          ? new Date(caltransRepFields.caltransRepSignedOn)
                          : null
                      }
                      dateFormat="MM/dd/yyyy (E)"
                      onChange={handleChange_date}
                      disabled={hasPrevSignature}
                    />
                    {formErrors.date && (
                      <span className="error-small">
                        <small>{formErrors.date}</small>
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="form-row form-group my-2">
              <label htmlFor="caltransRepName" className="col-form-label col-3">
                <b>Name:</b>*
              </label>
              <div className="col-9">
                <input
                  id="caltransRepName"
                  name="caltransRepName"
                  className="form-control"
                  onChange={handleChange_caltransRep}
                  onBlur={handleBlur_caltransRep}
                  value={caltransRepFields.caltransRepName}
                  disabled={hasPrevSignature}
                />
                {formErrors.name && (
                  <span className="error-small">
                    <small>{formErrors.name}</small>
                  </span>
                )}
              </div>
            </div>
            <div className="form-row form-group mb-0">
              <label htmlFor="caltransRepRemarks" className="col-form-label col-3">
                Remarks:
              </label>
              <div className="col-9">
                <input
                  id="caltransRepRemarks"
                  name="caltransRepRemarks"
                  maxLength={30}
                  className="form-control"
                  onChange={handleChange_caltransRep}
                  value={caltransRepFields.caltransRepRemarks}
                />
                <span className="text-small">
                  {`${(caltransRepFields.caltransRepRemarks || "").length}/30 characters`}
                </span>
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="bg-lightgray">
        <div className="form-row form-group">
          {hasPrevSignature && (
            <>
              <button
                id="taSignDelete"
                name="taSignDelete"
                className="btn btn-danger mr-2"
                onClick={handleClick_delete}
              >
                <FontAwesomeIcon icon="trash-alt" className="fa-fw mr-1" />
                Delete Signature
              </button>
              <span className="my-auto">|</span>
            </>
          )}
          <button
            id="taSignCancel"
            name="taSignCancel"
            className="btn btn-secondary mx-2"
            onClick={toggleModal}
          >
            Cancel
          </button>
          <button
            id="taSignOk"
            name="taSignOk"
            className="btn btn-primary"
            onClick={handleClick_ok}
            disabled={formIsIncomplete()}
          >
            OK
          </button>
        </div>
      </ModalFooter>
      {showAlert_signatureEmpty && (
        <AlertModal
          alertKey={"taSignatureEmptyAlert"}
          alertTitle={"Signature Incomplete"}
          alertTitleClass="bg-warning"
          alertTitleIcon="exclamation-triangle"
          alertText="There is no signature. Do you wish to exit?"
          acceptCaption={"Yes"}
          acceptClass={"danger"}
          provideReject={true}
          rejectCaption={"No"}
          rejectClass={"secondary"}
          handleAlertAction={handleAlert_signatureEmpty}
          showAlertModal={showAlert_signatureEmpty}
          toggleAlertModal={toggleAlert_signatureEmpty}
        />
      )}
      {showAlert_deleteSignatureConfirm && (
        <AlertModal
          alertKey={"taDeleteSignatureConfirm"}
          alertTitle={"Delete Signature"}
          alertTitleClass="bg-warning"
          alertTitleIcon="exclamation-triangle"
          alertText="Are you sure you wish to delete this signature?"
          acceptCaption={"Yes"}
          acceptClass={"danger"}
          provideReject={true}
          rejectCaption={"No"}
          rejectClass={"secondary"}
          handleAlertAction={handleAlert_deleteSignatureConfirm}
          showAlertModal={showAlert_deleteSignatureConfirm}
          toggleAlertModal={toggleAlert_deleteSignatureConfirm}
        />
      )}
      {showAlert_formIncomplete && (
        <AlertModal
          alertKey="taFormIncompleteAlert"
          alertTitle="Form Incomplete"
          alertTitleClass="bg-warning"
          alertTitleIcon="exclamation-triangle"
          alertText="Not all required fields are complete."
          acceptCaption={"OK"}
          acceptClass={"secondary"}
          provideReject={false}
          handleAlertAction={toggleAlert_formIncomplete}
          showAlertModal={showAlert_formIncomplete}
          toggleAlertModal={toggleAlert_formIncomplete}
        />
      )}
    </Modal>
  );
}

export default AdrTentativeAgreementSignModalV3;
